import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Sparkles, Check, RotateCcw } from 'lucide-react';
import './PackageQuiz.css';

const questions = [
  {
    id: 'business-type',
    question: 'What type of business do you run?',
    options: [
      { label: 'Restaurant, cafe, or food business', value: 'restaurant' },
      { label: 'Service-based (salon, gym, trainer, consultant)', value: 'service' },
      { label: 'Trade (plumber, electrician, builder)', value: 'trade' },
      { label: 'Launching a brand new business', value: 'new-business' },
      { label: 'Other / not sure', value: 'other' },
    ],
  },
  {
    id: 'current-website',
    question: 'Do you have a website already?',
    options: [
      { label: 'No — I need to get online from scratch', value: 'none' },
      { label: 'Yes, but it looks outdated', value: 'outdated' },
      { label: 'Yes, but I need a full rebrand', value: 'rebrand' },
    ],
  },
  {
    id: 'branding',
    question: 'Do you have a logo and brand identity?',
    options: [
      { label: 'No — I need a logo and brand created', value: 'need-brand' },
      { label: 'I have a logo but no full brand kit', value: 'partial-brand' },
      { label: 'Yes — I already have my branding sorted', value: 'has-brand' },
    ],
  },
  {
    id: 'features',
    question: 'What features do you need most?',
    options: [
      { label: 'Just a simple website with my info', value: 'simple' },
      { label: 'Website + professional branding', value: 'branding' },
      { label: 'Digital menu, reservations, QR codes', value: 'restaurant-features' },
      { label: 'Everything — email, booking, socials, the lot', value: 'everything' },
    ],
  },
  {
    id: 'budget',
    question: 'What\'s your budget range?',
    options: [
      { label: 'Under $1,000', value: 'low' },
      { label: '$1,000 – $2,000', value: 'mid' },
      { label: '$2,000 – $3,500', value: 'high' },
      { label: '$3,500+ (I want the best)', value: 'premium' },
    ],
  },
];

const getRecommendation = (answers) => {
  let score = { 'online-presence': 0, 'brand-website': 0, restaurant: 0, 'full-suite': 0 };
  let tierScore = { Basic: 0, Standard: 0, Premium: 0 };

  // Business type
  if (answers['business-type'] === 'restaurant') { score.restaurant += 3; }
  else if (answers['business-type'] === 'new-business') { score['full-suite'] += 2; score['brand-website'] += 1; }
  else if (answers['business-type'] === 'trade') { score['online-presence'] += 2; score['brand-website'] += 1; }
  else if (answers['business-type'] === 'service') { score['brand-website'] += 2; score['full-suite'] += 1; }
  else { score['brand-website'] += 1; score['online-presence'] += 1; }

  // Current website
  if (answers['current-website'] === 'none') { score['online-presence'] += 1; }
  else if (answers['current-website'] === 'rebrand') { score['full-suite'] += 2; score['brand-website'] += 1; }
  else if (answers['current-website'] === 'outdated') { score['brand-website'] += 1; }

  // Branding
  if (answers['branding'] === 'need-brand') { score['brand-website'] += 2; score['full-suite'] += 1; tierScore.Standard += 1; }
  else if (answers['branding'] === 'partial-brand') { score['brand-website'] += 1; }
  else { score['online-presence'] += 1; }

  // Features
  if (answers['features'] === 'simple') { score['online-presence'] += 2; tierScore.Basic += 1; }
  else if (answers['features'] === 'branding') { score['brand-website'] += 2; tierScore.Standard += 1; }
  else if (answers['features'] === 'restaurant-features') { score.restaurant += 3; tierScore.Standard += 1; }
  else if (answers['features'] === 'everything') { score['full-suite'] += 3; tierScore.Premium += 1; }

  // Budget
  if (answers['budget'] === 'low') { score['online-presence'] += 2; tierScore.Basic += 2; }
  else if (answers['budget'] === 'mid') { score['brand-website'] += 1; score.restaurant += 1; tierScore.Standard += 2; }
  else if (answers['budget'] === 'high') { score['full-suite'] += 1; score.restaurant += 1; tierScore.Standard += 1; tierScore.Premium += 1; }
  else if (answers['budget'] === 'premium') { score['full-suite'] += 2; tierScore.Premium += 2; }

  // Find winning package
  const packageId = Object.entries(score).sort((a, b) => b[1] - a[1])[0][0];
  const tierName = Object.entries(tierScore).sort((a, b) => b[1] - a[1])[0][0];

  return { packageId, tierName };
};

const packageDetails = {
  'online-presence': { name: 'Online Presence Pack', tagline: 'Just Get Me Online', reason: 'You need a clean, professional website to get your business found online without breaking the bank.' },
  'brand-website': { name: 'Brand + Website Pack', tagline: 'Look Professional', reason: 'You need a full brand identity and website that makes you stand out from competitors.' },
  'restaurant': { name: 'Restaurant Pro Pack', tagline: 'Restaurants & Cafes', reason: 'Your food business needs a specialist site with digital menus, reservations, and review integrations.' },
  'full-suite': { name: 'Full Business Suite', tagline: 'Launch My Business', reason: 'You want everything handled properly — brand, website, email, booking, and socials — all in one go.' },
};

const PackageQuiz = ({ onRecommend }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);
  const [visible, setVisible] = useState(false);

  // Show quiz section and auto-start when user clicks a #quiz link
  useEffect(() => {
    const handleHash = () => {
      if (window.location.hash === '#quiz') {
        setVisible(true);
        setTimeout(() => {
          document.getElementById('quiz')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const handleAnswer = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const rec = getRecommendation(newAnswers);
      setResult(rec);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
    setStarted(false);
  };

  const scrollToPackages = () => {
    if (onRecommend && result) {
      onRecommend(result.packageId);
    }
  };

  // Hidden by default until user clicks "Help Me Choose"
  if (!visible) {
    return null;
  }

  if (!started) {
    return (
      <section className="quiz section" id="quiz">
        <div className="container">
          <div className="quiz__intro">
            <span className="section-label">Not sure what you need?</span>
            <h2 className="section-title">Let us help you pick.</h2>
            <p className="section-subtitle">Answer 5 quick questions and we'll recommend the perfect package for your business. Takes 30 seconds.</p>
            <button className="btn-primary quiz__start-btn" onClick={() => setStarted(true)}>
              <Sparkles size={18} />
              Find My Package
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="quiz section" id="quiz">
      <div className="container">
        <div className="quiz__wrapper">
          {!result ? (
            <>
              {/* Progress */}
              <div className="quiz__progress">
                <div className="quiz__progress-bar">
                  <div className="quiz__progress-fill" style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }} />
                </div>
                <span className="quiz__progress-text">{currentQuestion + 1} of {questions.length}</span>
              </div>

              <div
                  key={currentQuestion}
                  className="quiz__question-card quiz__fade-in"
                >
                  <h3 className="quiz__question">{questions[currentQuestion].question}</h3>
                  <div className="quiz__options">
                    {questions[currentQuestion].options.map((opt) => (
                      <button
                        key={opt.value}
                        className={`quiz__option ${answers[questions[currentQuestion].id] === opt.value ? 'quiz__option--selected' : ''}`}
                        onClick={() => handleAnswer(questions[currentQuestion].id, opt.value)}
                      >
                        <span>{opt.label}</span>
                        <ArrowRight size={16} />
                      </button>
                    ))}
                  </div>

                  {currentQuestion > 0 && (
                    <button className="quiz__back-btn" onClick={handleBack}>
                      <ArrowLeft size={16} /> Previous question
                    </button>
                  )}
                </div>
            </>
          ) : (
            <div
              className="quiz__result quiz__fade-in"
            >
              <div className="quiz__result-icon">
                <Sparkles size={32} />
              </div>
              <h3 className="quiz__result-title">Great choice! Here's what we recommend:</h3>

              <div className="quiz__result-card">
                <span className="quiz__result-tagline">{packageDetails[result.packageId].tagline}</span>
                <h4 className="quiz__result-package">{packageDetails[result.packageId].name}</h4>
                <div className="quiz__result-tier">
                  <Check size={16} />
                  <span>{result.tierName} Tier</span>
                </div>
                <p className="quiz__result-reason">{packageDetails[result.packageId].reason}</p>
              </div>

              <p className="quiz__result-congrats">
                You've made a great choice. This package is built exactly for businesses like yours — it has everything you need to look professional and win more customers.
              </p>

              <div className="quiz__result-actions">
                <button className="btn-primary" onClick={scrollToPackages}>
                  View This Package
                  <ArrowRight size={16} />
                </button>
                <button className="btn-secondary" onClick={handleReset}>
                  <RotateCcw size={16} />
                  Start Over
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PackageQuiz;
